import type { ChatMessage, ChatSummary, FileItem } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertCircle, Paperclip, Send, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { chatMessagesQuery } from '@/lib/crm';
import { formatMoment, formatSize } from '@/lib/format';
import { ChatTicketButton } from './client-equipment';
import { ChannelMark } from './crm-bits';
import { Button, ErrorBox, Spinner } from './ui';

const isImage = (f: FileItem) => /^image\/(jpeg|png|webp|gif)$/.test(f.mime);

function Bubble({ m }: { m: ChatMessage }) {
  const out = m.direction === 'out';
  return (
    <li className={clsx('flex', out ? 'justify-end' : 'justify-start')}>
      <div
        className={clsx(
          'max-w-[85%] rounded-lg px-3 py-2 sm:max-w-[75%]',
          m.status === 'failed'
            ? 'border border-danger/40 bg-danger-soft text-ink'
            : out
              ? 'bg-graphite text-white'
              : 'border border-line bg-surface',
        )}
      >
        {m.files.map((f) =>
          isImage(f) ? (
            <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="mb-1.5 block">
              <img src={f.url} alt={f.name} className="max-h-60 rounded-md object-contain" loading="lazy" />
            </a>
          ) : (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className={clsx(
                'mb-1 flex items-center gap-1.5 text-[13.5px] underline-offset-2 hover:underline',
                out && m.status !== 'failed' ? 'text-white' : 'text-link',
              )}
            >
              <Paperclip className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{f.name}</span>
              <span className="shrink-0 opacity-70">{formatSize(f.size)}</span>
            </a>
          ),
        )}
        {m.text && <p className="whitespace-pre-wrap break-words text-[14px] leading-snug">{m.text}</p>}
        <p
          className={clsx(
            'mt-1 text-[11.5px]',
            out && m.status !== 'failed' ? 'text-[#b9c5ce]' : 'text-steel',
          )}
        >
          {out && (m.author?.fullName ?? 'Автоответ')}
          {out && ' · '}
          {formatMoment(m.at)}
        </p>
        {m.status === 'failed' && (
          <p className="mt-1 flex items-start gap-1 text-[12.5px] text-danger">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            Не доставлено: {m.error}
          </p>
        )}
      </div>
    </li>
  );
}

/** Переписка с клиентом: сообщения сверху вниз, поле ответа внизу. Открытие чата сбрасывает «непрочитано». */
export function ChatPanel({ chat, className }: { chat: ChatSummary; className?: string }) {
  const qc = useQueryClient();
  const messages = useQuery(chatMessagesQuery(chat.id));
  const [text, setText] = useState('');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: отмечаем прочитанным при открытии и при новых входящих
  useEffect(() => {
    if (chat.unread > 0)
      api(`/crm/chats/${chat.id}/read`, { method: 'POST' }).then(() =>
        qc.invalidateQueries({ queryKey: ['crm', 'counters'] }),
      );
  }, [chat.id, chat.unread]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка вниз при новых сообщениях
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [messages.data?.length]);

  const send = useMutation({
    mutationFn: () =>
      api(`/crm/chats/${chat.id}/messages`, {
        method: 'POST',
        body: { text, fileIds: files.map((f) => f.id) },
      }),
    onSuccess: () => {
      setText('');
      setFiles([]);
      setError(null);
    },
    onError: (err) => setError(err.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });

  async function upload(list: FileList | null) {
    if (!list?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(list).slice(0, 5)) {
        const fd = new FormData();
        fd.append('file', f);
        const item = await api<FileItem>('/files', { method: 'POST', body: fd });
        setFiles((prev) => [...prev, item]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const canSend = (text.trim() || files.length) && !send.isPending && !uploading;
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) send.mutate();
    }
  };

  return (
    <section
      className={clsx('panel flex min-h-0 flex-col', className)}
      aria-label={`Переписка: ${chat.title}`}
    >
      <header className="flex items-center gap-2 border-b border-line px-4 py-2.5">
        <ChannelMark channel={chat.channel} withLabel />
        <h2 className="min-w-0 truncate font-semibold">{chat.title}</h2>
        {chat.username && <span className="truncate text-[13px] text-steel">@{chat.username}</span>}
        <ChatTicketButton chat={chat} />
      </header>
      <div ref={list} className="min-h-[220px] flex-1 overflow-y-auto bg-paper/60 px-3 py-3">
        {messages.isPending ? (
          <Spinner />
        ) : messages.error ? (
          <ErrorBox error={messages.error} />
        ) : messages.data.length === 0 ? (
          <p className="py-8 text-center text-[13.5px] text-steel">Сообщений пока нет</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {messages.data.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
          </ul>
        )}
      </div>
      <form
        className="border-t border-line p-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) send.mutate();
        }}
      >
        {files.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-1.5">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-1.5 rounded bg-sunken px-2 py-1 text-[12.5px]">
                <Paperclip className="size-3.5 text-steel" aria-hidden />
                <span className="max-w-[160px] truncate">{f.name}</span>
                <button
                  type="button"
                  aria-label={`Убрать ${f.name}`}
                  onClick={() => setFiles(files.filter((x) => x.id !== f.id))}
                  className="text-steel hover:text-danger"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="mb-2 text-[13px] text-danger">
            {error}
          </p>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInput}
            type="file"
            multiple
            className="sr-only"
            onChange={(e) => upload(e.target.files)}
            aria-label="Прикрепить файл"
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            aria-label="Прикрепить файл"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-steel hover:bg-sunken hover:text-ink"
          >
            <Paperclip className="size-[18px]" />
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={Math.min(5, Math.max(1, text.split('\n').length))}
            maxLength={4000}
            placeholder="Ответ клиенту — уйдёт от имени бота. Enter — отправить, Shift+Enter — новая строка"
            aria-label="Сообщение"
            className="input h-auto min-h-9 flex-1 resize-none py-1.5 leading-snug"
          />
          <Button
            type="submit"
            disabled={!canSend}
            loading={send.isPending}
            aria-label="Отправить"
            className="px-3"
          >
            <Send className="size-4" aria-hidden />
          </Button>
        </div>
      </form>
    </section>
  );
}
